import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
    Smartphone, 
    Shield, 
    Truck, 
    Clock, 
    CheckCircle, 
    Users, 
    Package,
    ArrowRight,
    Building2,
    BadgeCheck,
    Zap,
    BarChart3,
    Search,
    FileText,
    Wrench,
    Store,
    Battery,
    Eye,
    AlertCircle,
    Star,
    Award,
    Globe,
    ShieldCheck,
    Timer,
    CircleDollarSign,
    HeartHandshake,
    ClipboardCheck
} from 'lucide-react';

const HomePage = () => {
    const { isAuthenticated, isAdmin } = useAuth();

    // Images from Unsplash
    const images = {
        hero: "https://customer-assets.emergentagent.com/job_dealer-hub-phones/artifacts/pt3l5rc7_vGRkt0BsELxTnaglq3_aV.png",
        testing: "https://images.unsplash.com/photo-1755054954360-400777d6ff51?w=600&q=80",
        warehouse: "https://images.unsplash.com/photo-1695978919095-410032e30b1d?w=600&q=80",
        store: "https://images.unsplash.com/photo-1645812260763-8c73d615015c?w=600&q=80",
        phones: "https://images.unsplash.com/photo-1695978918861-6f93447068bc?w=600&q=80"
    };

    const testingFeatures = [
        { icon: Eye, title: "État esthétique précis", desc: "Châssis, écran, vitre arrière documentés" },
        { icon: Battery, title: "Niveau de batterie", desc: "Pourcentage exact ou fourchette indiqué" },
        { icon: AlertCircle, title: "Pannes identifiées", desc: "Face ID, tactile, caméra, haut-parleur..." },
        { icon: ClipboardCheck, title: "Grade cosmétique", desc: "Classification A, B, C claire" },
        { icon: Shield, title: "Statut lock/unlock", desc: "Vérifié et documenté" },
        { icon: Wrench, title: "Pièces manquantes", desc: "Tout est mentionné explicitement" }
    ];

    const advantages = [
        { icon: Search, title: "Vente à l'unité", desc: "Pas de lot imposé, achetez dès 1 appareil" },
        { icon: Zap, title: "Stock temps réel", desc: "Synchronisé en permanence avec notre ERP" },
        { icon: Truck, title: "Livraison 24h", desc: "Expédition express partout en France" },
        { icon: FileText, title: "Facturation B2B", desc: "Virement, CB, TVA marge et classique" },
        { icon: ShieldCheck, title: "Accès sécurisé", desc: "SIRET obligatoire, validation sous 24h" },
        { icon: Award, title: "+10 ans d'expertise", desc: "Spécialiste du marché français" }
    ];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white sticky top-0 z-50 border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-600 rounded-lg p-1 shadow-md">
                                <div className="border-2 border-white rounded px-3 py-0.5">
                                    <span className="font-black text-lg text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                                        PHONEB2B
                                    </span>
                                </div>
                            </div>
                            <span className="hidden sm:block text-xs text-slate-500 font-medium">Réservé aux professionnels</span>
                        </div>
                        
                        <div className="flex items-center gap-2 sm:gap-3">
                            {isAuthenticated ? (
                                <Link to={isAdmin ? '/admin' : '/catalog'}>
                                    <Button className="rounded-lg bg-blue-600 text-white font-bold px-4 sm:px-6 hover:bg-blue-700" data-testid="go-to-app">
                                        <span className="hidden sm:inline">Accéder à l'espace {isAdmin ? 'admin' : 'client'}</span>
                                        <span className="sm:hidden">Mon espace</span>
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login">
                                        <Button variant="ghost" className="text-slate-600 hover:text-blue-600 font-medium rounded-lg" data-testid="home-login">
                                            Connexion
                                        </Button>
                                    </Link>
                                    <Link to="/register">
                                        <Button className="rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700" data-testid="home-register">
                                            <span className="hidden sm:inline">Demander un compte</span>
                                            <span className="sm:hidden">S'inscrire</span>
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative py-16 lg:py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
                </div>
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-blue-400/30">
                                <BadgeCheck className="w-4 h-4 text-blue-300" />
                                <span className="text-sm font-medium text-blue-200">Réservé aux revendeurs & reconditionneurs</span>
                            </div>
                            
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                                Smartphones<br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-300">
                                    prêts à vendre
                                </span>
                                <span className="text-white"> ou </span>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">
                                    à réparer
                                </span>
                            </h1>
                            
                            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed max-w-xl">
                                <strong className="text-white">Stock B2B pour professionnels du mobile.</strong> Chaque appareil est testé, 
                                documenté et classé : fonctionnel pour la revente directe ou en panne pour vos ateliers de réparation.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                <Link to={isAuthenticated ? '/catalog' : '/register'}>
                                    <Button className="w-full sm:w-auto rounded-xl bg-blue-500 text-white font-bold px-8 py-6 text-lg hover:bg-blue-400 shadow-lg shadow-blue-500/30" data-testid="hero-cta">
                                        {isAuthenticated ? 'Voir le catalogue' : 'Demander un accès pro'}
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                                {!isAuthenticated && (
                                    <Link to="/login">
                                        <Button variant="outline" className="w-full sm:w-auto rounded-xl border-2 border-slate-500 text-white font-semibold px-8 py-6 text-lg hover:bg-white/10">
                                            J'ai déjà un compte
                                        </Button>
                                    </Link>
                                )}
                            </div>

                            {/* Trust badges */}
                            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    <span>SIRET vérifié</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    <span>+10 ans d'expérience</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    <span>Livraison 24h</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Hero Image */}
                        <div className="hidden lg:block relative">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img 
                                    src={images.hero}
                                    alt="Test et diagnostic de smartphones"
                                    className="w-full h-[400px] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                                <div className="absolute bottom-6 left-6 right-6">
                                    <div className="bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-slate-900 text-sm">Test complet effectué</p>
                                                <p className="text-xs text-slate-500">Diagnostic logiciel + contrôle manuel</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Floating stats */}
                            <div className="absolute -top-4 -right-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 shadow-xl border border-emerald-400/30">
                                <div className="flex items-center gap-2 text-white">
                                    <Package className="w-5 h-5" />
                                    <div>
                                        <div className="text-lg font-bold">Arrivage</div>
                                        <div className="text-xs text-emerald-100">quotidien</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Two Product Types Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            Deux types de produits, selon vos besoins
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Que vous soyez revendeur ou réparateur, nous avons le stock adapté à votre activité.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Functional devices */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
                            <div className="h-48 bg-gradient-to-br from-emerald-500 to-teal-600 relative overflow-hidden">
                                <img 
                                    src={images.store}
                                    alt="Boutique de téléphones"
                                    className="w-full h-full object-cover mix-blend-overlay opacity-60"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                                        <Store className="w-16 h-16 text-white" />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-3 py-1 text-xs font-medium mb-3">
                                    <CheckCircle className="w-3 h-3" />
                                    Prêt à revendre
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">Appareils fonctionnels</h3>
                                <p className="text-slate-600 mb-4">
                                    Pour les <strong>boutiques et distributeurs</strong> qui veulent du stock directement revendable, sans intervention technique.
                                </p>
                                <ul className="space-y-2 text-sm text-slate-600">
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        Testés et validés à 100%
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        Prêts à l'emploi immédiat
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        TVA sur marge et TVA classique
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Devices to repair */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
                            <div className="h-48 bg-gradient-to-br from-blue-500 to-indigo-600 relative overflow-hidden">
                                <img 
                                    src={images.hero}
                                    alt="Réparation de téléphones"
                                    className="w-full h-full object-cover mix-blend-overlay opacity-60"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                                        <Wrench className="w-16 h-16 text-white" />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-3 py-1 text-xs font-medium mb-3">
                                    <Wrench className="w-3 h-3" />
                                    À réparer
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">Appareils à réparer</h3>
                                <p className="text-slate-600 mb-4">
                                    Pour les <strong>réparateurs et reconditionneurs</strong>. Pannes identifiées et documentées en détail.
                                </p>
                                <ul className="space-y-2 text-sm text-slate-600">
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                        Niveau 1 : Écran, batterie, caméra
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                        Niveau 2 : Face ID, chassis, pannes longues
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                        Niveau 3 : Micro-soudure, carte mère
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Work With Us Section */}
            <section className="py-16 lg:py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 rounded-full px-4 py-2 mb-4 text-sm font-medium">
                            <Eye className="w-4 h-4" />
                            Transparence totale
                        </div>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            Pourquoi travailler avec nous ?
                        </h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Maîtrisez vos achats. Chaque appareil est documenté en détail : 
                            <strong className="text-slate-900"> vous savez exactement ce que vous achetez avant de commander.</strong>
                        </p>
                    </div>

                    {/* Main value proposition */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 mb-10">
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Battery className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-900 mb-1">État de batterie</h4>
                                    <p className="text-sm text-slate-500">Pourcentage exact indiqué sur chaque fiche</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-900 mb-1">Messages Apple</h4>
                                    <p className="text-sm text-slate-500">"Pièce inconnue" ou autre - tout est mentionné</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Wrench className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-900 mb-1">Pannes détaillées</h4>
                                    <p className="text-sm text-slate-500">Face ID, écran, caméra... rien n'est caché</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Eye className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-900 mb-1">État esthétique précis</h4>
                                    <p className="text-sm text-slate-500">Châssis, écran, vitre arrière documentés</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <BarChart3 className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-900 mb-1">Évaluez votre opération</h4>
                                    <p className="text-sm text-slate-500">Temps, compétences, marge - vous décidez</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Search className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-900 mb-1">Achat à l'unité</h4>
                                    <p className="text-sm text-slate-500">Sélectionnez uniquement ce dont vous avez besoin</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Two profiles */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Reseller profile */}
                        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <Store className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">Vous êtes revendeur ?</h3>
                                    <p className="text-emerald-100 text-sm">Pas le temps de réparer</p>
                                </div>
                            </div>
                            <p className="text-emerald-50 mb-4">
                                Alimentez votre vitrine avec des appareils <strong>prêts à vendre</strong>. 
                                Stock testé, fonctionnel, directement revendable à vos clients.
                            </p>
                            <div className="flex items-center gap-2 text-sm text-emerald-100">
                                <CheckCircle className="w-4 h-4" />
                                <span>Zéro intervention technique nécessaire</span>
                            </div>
                        </div>

                        {/* Repair shop profile */}
                        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <Wrench className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">Vous avez un atelier ?</h3>
                                    <p className="text-blue-100 text-sm">Du temps entre deux réparations</p>
                                </div>
                            </div>
                            <p className="text-blue-50 mb-4">
                                Valorisez votre temps ! Réparez nos appareils en panne et revendez-les pour 
                                <strong> maximiser vos marges</strong>. Nouveau challenge, nouvelle source de revenus.
                            </p>
                            <div className="flex items-center gap-2 text-sm text-blue-100">
                                <CheckCircle className="w-4 h-4" />
                                <span>Pannes clairement identifiées = pas de surprise</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Advantages Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            Pourquoi nous choisir ?
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Une plateforme pensée par et pour les professionnels du mobile.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {advantages.map((adv, index) => (
                            <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all group">
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                                    <adv.icon className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="font-bold text-slate-900 mb-2">{adv.title}</h3>
                                <p className="text-sm text-slate-600">{adv.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 lg:py-20 bg-blue-600">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                        Prêt à sourcer différemment ?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                        Rejoignez les professionnels qui ont choisi la transparence. 
                        Accès réservé aux détenteurs de SIRET.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to={isAuthenticated ? '/catalog' : '/register'}>
                            <Button className="w-full sm:w-auto rounded-xl bg-white text-blue-600 font-bold px-8 py-6 text-lg hover:bg-blue-50 shadow-lg" data-testid="cta-register">
                                {isAuthenticated ? 'Accéder au catalogue' : 'Demander mon accès pro'}
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                    </div>
                    
                    <p className="text-sm text-blue-200 mt-6">
                        Validation sous 24h • Aucun engagement • Accès immédiat après approbation
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-slate-900 text-slate-400 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="bg-blue-600 rounded-lg p-1 shadow-md inline-block mb-4">
                                <div className="border-2 border-white rounded px-3 py-0.5">
                                    <span className="font-black text-lg text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                                        PHONEB2B
                                    </span>
                                </div>
                            </div>
                            <p className="text-sm">
                                Plateforme B2B de vente de smartphones testés. Réservé aux professionnels.
                            </p>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold text-white mb-4">Produits</h4>
                            <ul className="space-y-2 text-sm">
                                <li>Smartphones fonctionnels</li>
                                <li>Appareils à réparer</li>
                                <li>Stock temps réel</li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold text-white mb-4">Services</h4>
                            <ul className="space-y-2 text-sm">
                                <li>Livraison 24h</li>
                                <li>Facturation B2B</li>
                                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold text-white mb-4">Légal</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/cgv" className="hover:text-white">CGV</Link></li>
                                <li><Link to="/privacy" className="hover:text-white">Confidentialité & Cookies</Link></li>
                                <li><Link to="/mentions" className="hover:text-white">Mentions légales</Link></li>
                                <li><Link to="/grades" className="hover:text-white">Guide des grades</Link></li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="border-t border-slate-800 pt-8 text-center text-sm">
                        <p>© {new Date().getFullYear()} PHONEB2B. Tous droits réservés. Plateforme réservée aux professionnels.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePage;
